import { useCallback, useEffect, useState } from 'react';

import { searchCatalogProductsByGtins } from 'apis/offer-listing-service/api';
import { Product } from 'apis/offer-listing-service/types';
import { isDefined } from 'common/utils';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { buildProductOverriddenOffer } from 'modules/shared-menu/pages/SharedMenuEditorPage/transformers';
import { Offer } from 'modules/shared-menu/pages/SharedMenuEditorPage/types';

type Props = {
  selectedIds: Set<string>;
  offers: Offer[];
};

const GTIN14_LENGTH = 14;

// Product catalog uses GTIN-14 format
const getGtin14 = (gtin: string | undefined) => gtin?.padStart(GTIN14_LENGTH, '0');

const useBulkProductConnect = ({ selectedIds, offers }: Props) => {
  const scope = useScopeContext();
  const [isLoading, setIsLoading] = useState(false);
  const [matchingGtinOffers, setMatchingGtinOffers] = useState<Offer[]>([]);
  const [foundProducts, setFoundProducts] = useState<Product[]>([]);
  const [noMatchingGtin, setNoMatchingGtin] = useState(0);
  const noGtins = offers.filter(offer => !offer.barcode_gtin && selectedIds.has(offer.id)).length;
  const alreadyConnected = offers.filter(offer => offer.product_id && selectedIds.has(offer.id)).length;

  const fetchProducts = useCallback(
    async (gtins: string[]) => {
      setIsLoading(true);
      const products = (await searchCatalogProductsByGtins(gtins, scope)).filter(p => p);
      setFoundProducts(products);
      const matchingGtinOffers = offers
        .filter(offer => offer.barcode_gtin && products.find(p => p.gtin === getGtin14(offer.barcode_gtin)))
        .map(offer => {
          const product = products.find(p => p.gtin === getGtin14(offer.barcode_gtin));
          return {
            ...buildProductOverriddenOffer(offer, product),
            product_id: product?.id,
            barcode_gtin: product?.gtin,
          };
        })
        .filter(isDefined);
      setMatchingGtinOffers(matchingGtinOffers);
      setNoMatchingGtin(gtins.length - products.length);
      setIsLoading(false);
    },
    [offers, scope],
  );

  useEffect(() => {
    const gtinsNeedToConnect = offers
      .filter(offer => !offer.product_id && selectedIds.has(offer.id))
      .map(offer => offer.barcode_gtin)
      .filter(isDefined);

    if (gtinsNeedToConnect.length > 0) {
      fetchProducts(gtinsNeedToConnect);
    }
    // This will run once when the modal mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isLoadingProducts: isLoading,
    matchingGtinOffers,
    noMatchingGtin,
    noGtins,
    alreadyConnected,
    foundProducts,
  };
};

export default useBulkProductConnect;
